import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useOutletContext, useParams } from 'react-router-dom';
import type { OutcomeInstrument } from '@lineageatlas/shared';
import { isFeatureEnabled } from '../../config/featureFlags';
import {
  createOutcomeMeasurement,
  listOutcomeInstruments,
  listOutcomeMeasurements,
  type OutcomeMeasurementWithJoins,
} from '../../lib/api/regenloop';
import type { ClinicianScopedView, ClinicianVisiblePatient } from './data';
import { formatClinicianDateTime } from './data';

export interface ClinicianOutletContext {
  view: ClinicianScopedView;
}

type PiTab = 'today' | 'patients' | 'log' | 'alerts' | 'profile';
type PiIcon =
  | 'alert'
  | 'arrow'
  | 'camera'
  | 'check'
  | 'clock'
  | 'comment'
  | 'dose'
  | 'intel'
  | 'measure'
  | 'patient'
  | 'plus'
  | 'profile'
  | 'search'
  | 'shield';

const LOOP_STAGES = ['Sour', 'Prod', 'Acqu', 'Admi', 'Meas', 'Lear'];
const TIMEPOINTS = ['Baseline', 'Week 2', 'Week 6', 'Week 12', 'Month 4'];
const FALLBACK_INSTRUMENTS = ['KOOS', 'VAS', 'PROMIS', 'ASES'];

type ApiLoadState<T> = {
  data: T | null;
  error: Error | null;
  loading: boolean;
};

function useClinicianView(): ClinicianScopedView {
  return useOutletContext<ClinicianOutletContext>().view;
}

function useScopedPatient() {
  const { id } = useParams<{ id: string }>();
  const view = useClinicianView();
  return {
    view,
    patient: view.patients.find((candidate) => candidate.id === id),
  };
}

function useTypedOutcomeInstruments(enabled: boolean): ApiLoadState<OutcomeInstrument[]> {
  const [state, setState] = useState<ApiLoadState<OutcomeInstrument[]>>({
    data: null,
    error: null,
    loading: false,
  });

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, error: null, loading: false });
      return;
    }

    let active = true;
    setState((current) => ({ ...current, error: null, loading: true }));
    void listOutcomeInstruments()
      .then((response) => {
        if (active) setState({ data: response.data, error: null, loading: false });
      })
      .catch((error: unknown) => {
        if (!active) return;
        setState({
          data: null,
          error: error instanceof Error ? error : new Error('Unable to load outcome instruments'),
          loading: false,
        });
      });

    return () => {
      active = false;
    };
  }, [enabled]);

  return state;
}

function useTypedOutcomeMeasurements({
  enabled,
  patientCode,
}: {
  enabled: boolean;
  patientCode?: string;
}): ApiLoadState<OutcomeMeasurementWithJoins[]> & {
  appendMeasurement: (measurement: OutcomeMeasurementWithJoins) => void;
} {
  const [state, setState] = useState<ApiLoadState<OutcomeMeasurementWithJoins[]>>({
    data: null,
    error: null,
    loading: false,
  });

  useEffect(() => {
    if (!enabled || !patientCode) {
      setState({ data: null, error: null, loading: false });
      return;
    }

    let active = true;
    setState((current) => ({ ...current, error: null, loading: true }));
    void listOutcomeMeasurements({ patient_id: patientCode })
      .then((response) => {
        if (active) setState({ data: response.data, error: null, loading: false });
      })
      .catch((error: unknown) => {
        if (!active) return;
        setState({
          data: null,
          error: error instanceof Error ? error : new Error('Unable to load outcome history'),
          loading: false,
        });
      });

    return () => {
      active = false;
    };
  }, [enabled, patientCode]);

  return {
    ...state,
    appendMeasurement: (measurement: OutcomeMeasurementWithJoins) => {
      setState((current) => ({
        data: [measurement, ...(current.data ?? [])],
        error: current.error,
        loading: current.loading,
      }));
    },
  };
}

function riskLabel(risk: ClinicianVisiblePatient['risk']): string {
  if (risk === 'urgent') return 'Review';
  if (risk === 'watch') return 'Watch';
  return 'Steady';
}

function riskClass(risk: ClinicianVisiblePatient['risk']): string {
  if (risk === 'urgent') return 'neg';
  if (risk === 'watch') return 'warn';
  return 'pos';
}

function patientDisplay(patient: ClinicianVisiblePatient): string {
  return patient.code || patient.id;
}

function physicianFirstName(name: string): string {
  return name.split(/[,\s]/).filter(Boolean)[0] ?? name;
}

function safeDateLabel(iso: string): string {
  return formatClinicianDateTime(iso).replace(',', '');
}

function responseIndex(patient: ClinicianVisiblePatient): number {
  if (patient.risk === 'urgent') return 54;
  if (patient.risk === 'watch') return 68;
  return 84;
}

function responseDelta(patient: ClinicianVisiblePatient): string {
  if (patient.risk === 'urgent') return '-3';
  if (patient.risk === 'watch') return '+6';
  return '+18';
}

function courseLot(patient: ClinicianVisiblePatient): string {
  return patient.courses[0]?.id.replace('course-', 'LA-').toUpperCase() ?? 'Pending';
}

function activeCourse(patient: ClinicianVisiblePatient) {
  return patient.courses[0];
}

function PiIcon({ name }: { name: PiIcon }) {
  const paths: Record<PiIcon, JSX.Element> = {
    alert: (
      <>
        <path d="M12 3 21 19H3L12 3Z" />
        <path d="M12 8v5M12 16h.01" />
      </>
    ),
    arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
    camera: (
      <>
        <path d="M4 8h3l2-3h6l2 3h3v11H4Z" />
        <circle cx="12" cy="13" r="3.5" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    clock: (
      <>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    comment: (
      <>
        <path d="M5 5h14v10H9l-4 4Z" />
        <path d="M8 9h8M8 12h5" />
      </>
    ),
    dose: (
      <>
        <path d="M7 17 17 7M14 4l6 6M4 14l6 6" />
        <path d="m9 15 2 2" />
      </>
    ),
    intel: (
      <>
        <path d="M12 4l1.7 4.3L18 10l-4.3 1.7L12 16l-1.7-4.3L6 10l4.3-1.7Z" />
        <circle cx="17.5" cy="6.5" r="1.3" />
      </>
    ),
    measure: (
      <>
        <path d="M4 17 9 12l3 3 7-8" />
        <path d="M4 21h16" />
      </>
    ),
    patient: (
      <>
        <circle cx="12" cy="8" r="3" />
        <path d="M5 20a7 7 0 0 1 14 0" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    profile: (
      <>
        <circle cx="12" cy="8" r="3" />
        <path d="M7 21h10M6 18a6 6 0 0 1 12 0" />
      </>
    ),
    search: (
      <>
        <circle cx="10.5" cy="10.5" r="6" />
        <path d="m15 15 4 4" />
      </>
    ),
    shield: <path d="M12 3 19 6v5c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6Z" />,
  };

  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      {paths[name]}
    </svg>
  );
}

function PiStatusBar() {
  return (
    <div className="pi-status">
      <span className="left num">9:41</span>
      <span className="dots" aria-hidden="true">
        <svg viewBox="0 0 18 12">
          <rect height="5" rx="1" width="3" x="0" y="7" />
          <rect height="7.5" rx="1" width="3" x="5" y="4.5" />
          <rect height="10" rx="1" width="3" x="10" y="2" />
          <rect height="12" opacity="0.4" rx="1" width="3" x="15" y="0" />
        </svg>
        <svg viewBox="0 0 16 12">
          <path d="M8 11.5 0.8 4.2a10 10 0 0 1 14.4 0z" />
        </svg>
        <svg viewBox="0 0 26 13">
          <rect fill="none" height="11" rx="3" stroke="currentColor" width="21" x="0.5" y="1" />
          <rect height="7" rx="1.5" width="16" x="2.5" y="3" />
          <rect height="4" rx="1" width="2" x="23" y="4.5" />
        </svg>
      </span>
    </div>
  );
}

function PiNav({
  title,
  backTo,
  right,
}: {
  title: string;
  backTo?: string;
  right?: JSX.Element;
}) {
  return (
    <div className="pi-nav">
      {backTo ? (
        <Link aria-label="Back" className="back" to={backTo}>
          <PiIcon name="arrow" />
        </Link>
      ) : (
        <div className="pi-nav-space" />
      )}
      <div className="ttl">{title}</div>
      {right ?? <div className="pi-nav-space" />}
    </div>
  );
}

function PiTabBar({ active }: { active: PiTab }) {
  const tabs: Array<{ active: PiTab; aria: string; icon: PiIcon; label: string; to: string }> = [
    { active: 'today', aria: 'Today', icon: 'measure', label: 'Today', to: '/clinician' },
    {
      active: 'patients',
      aria: 'My Patients',
      icon: 'patient',
      label: 'Patients',
      to: '/clinician/patients',
    },
    { active: 'alerts', aria: 'Alerts', icon: 'alert', label: 'Alerts', to: '/clinician' },
    { active: 'profile', aria: 'Me', icon: 'profile', label: 'Profile', to: '/clinician/me' },
  ];

  return (
    <nav aria-label="Clinician tabs" className="pi-tabbar">
      <Link aria-label="Today" className={`pi-tab ${active === 'today' ? 'on' : ''}`} to="/clinician">
        <PiIcon name="measure" />
        <span className="lb">Today</span>
      </Link>
      <Link
        aria-label="My Patients"
        className={`pi-tab ${active === 'patients' ? 'on' : ''}`}
        to="/clinician/patients"
      >
        <PiIcon name="patient" />
        <span className="lb">Patients</span>
      </Link>
      <Link aria-label="Log" className={`pi-tab fab ${active === 'log' ? 'on' : ''}`} to="/clinician/log">
        <span className="fabbtn">
          <PiIcon name="plus" />
        </span>
      </Link>
      {tabs.slice(2).map((tab) => (
        <Link
          aria-label={tab.aria}
          className={`pi-tab ${active === tab.active ? 'on' : ''}`}
          key={tab.active}
          to={tab.to}
        >
          <PiIcon name={tab.icon} />
          <span className="lb">{tab.label}</span>
        </Link>
      ))}
    </nav>
  );
}

function PiScreen({
  active,
  actionBar,
  children,
  compact = false,
}: {
  active: PiTab;
  actionBar?: JSX.Element;
  children: JSX.Element;
  compact?: boolean;
}) {
  return (
    <div className="pi-stage clinician-pi-stage">
      <section className={`pi-screen rl rl-light ${compact ? 'clinician-pi-compact' : ''}`}>
        <PiStatusBar />
        <div className="pi-scroll">{children}</div>
        {actionBar}
        <PiTabBar active={active} />
      </section>
    </div>
  );
}

function Head({
  eyebrow,
  title,
  avatar,
}: {
  avatar?: string;
  eyebrow: string;
  title: JSX.Element | string;
}) {
  return (
    <div className="pi-head">
      <div className="row">
        <div>
          <div className="pi-eyebrow">{eyebrow}</div>
          <h1 className="pi-h1">{title}</h1>
        </div>
        {avatar && <span className="pi-avatar">{avatar}</span>}
      </div>
    </div>
  );
}

function PatientRow({ patient }: { patient: ClinicianVisiblePatient }) {
  const course = activeCourse(patient);
  const status = riskClass(patient.risk);

  return (
    <Link className="pi-row" to={`/clinician/patients/${patient.id}`}>
      <span className={`av ${status === 'pos' ? 'acc' : ''}`}>{patient.initials}</span>
      <div className="main">
        <div className="t">{patientDisplay(patient)}</div>
        <div className="s">
          <span className="pid">{patient.code}</span> - {patient.indication}
          {course ? ` - ${course.nextMilestone}` : ''}
        </div>
      </div>
      <div className="trail">
        <MiniSpark patient={patient} />
        <span className={`pi-delta ${status}`}>{responseDelta(patient)}</span>
      </div>
    </Link>
  );
}

function MiniSpark({ patient }: { patient: ClinicianVisiblePatient }) {
  const base = patient.risk === 'urgent' ? [55, 52, 51, 49, 50] : patient.risk === 'watch' ? [58, 60, 62, 61, 64] : [54, 60, 66, 74, 84];
  const max = Math.max(...base);
  const min = Math.min(...base);
  const points = base
    .map((value, index) => {
      const x = (index / (base.length - 1)) * 60 + 2;
      const y = 20 - ((value - min) / Math.max(1, max - min)) * 16 + 1;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg aria-hidden="true" className="pi-spark" viewBox="0 0 64 24">
      <polyline className={`clinician-spark-line ${riskClass(patient.risk)}`} points={points} />
    </svg>
  );
}

function RoleBar({ view }: { view: ClinicianScopedView }) {
  return (
    <div className="pi-rolebar">
      <PiIcon name="shield" />
      {view.physician.title} - scoped to {view.physician.clinicIds.length} clinic
      {view.physician.clinicIds.length === 1 ? '' : 's'}
    </div>
  );
}

function Disclaimer() {
  return (
    <div className="pi-disc">
      <PiIcon name="shield" />
      <div>
        Research and quality-support intelligence, not medical advice or a diagnostic device. A
        clinician confirms every decision.
      </div>
    </div>
  );
}

function PreviewNote({ children }: { children: JSX.Element | string }) {
  return (
    <div className="pi-preview-note">
      <PiIcon name="shield" />
      <div>{children}</div>
    </div>
  );
}

export function ClinicianTodayPage() {
  const view = useClinicianView();
  const attention = view.patients.filter((patient) => patient.risk !== 'stable');

  return (
    <PiScreen active="today">
      <>
        <Head
          avatar={view.physician.name
            .split(/[,\s]/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0])
            .join('')}
          eyebrow="Today - physician mobile"
          title={
            <>
              Good morning,
              <br />
              Dr. {physicianFirstName(view.physician.name)}
            </>
          }
        />
        <RoleBar view={view} />
        <div className="pi-body">
          <Link className="pi-hero vio" to="/clinician/patients">
            <svg aria-hidden="true" className="loopmark" fill="none" viewBox="0 0 32 32">
              <circle cx="16" cy="16" r="13" stroke="#fff" strokeWidth="1.5" />
              <path d="M16 3a13 13 0 0 1 11 6" stroke="#fff" strokeLinecap="round" strokeWidth="2.4" />
              <circle cx="16" cy="16" fill="#fff" r="4" />
            </svg>
            <div className="tag">
              <PiIcon name="measure" />
              New results posted
            </div>
            <h2>Your active panel needs review</h2>
            <p>
              {attention.length || view.today.watchCount} patient signal
              {(attention.length || view.today.watchCount) === 1 ? '' : 's'} need clinician
              follow-up today.
            </p>
            <span className="cta">
              Review panel <PiIcon name="arrow" />
            </span>
          </Link>

          <div aria-label="Today summary" className="pi-stats">
            <div className="pi-stat">
              <div className="v">{view.patients.length}</div>
              <div className="l">Active patients</div>
            </div>
            <div className="pi-stat">
              <div className="v">{view.today.dueMilestones.length}</div>
              <div className="l">Sessions today</div>
            </div>
            <div className="pi-stat">
              <div className="v warn">{view.today.watchCount}</div>
              <div className="l">Follow-ups due</div>
            </div>
          </div>

          <div className="pi-sec">Today's sessions</div>
          <div className="pi-list">
            {view.today.dueMilestones.slice(0, 3).map((milestone) => (
              <Link className="pi-row" key={`${milestone.patientId}-${milestone.label}`} to={`/clinician/patients/${milestone.patientId}`}>
                <div className="clinician-time num">{safeDateLabel(milestone.at).split(' ').slice(-2).join(' ')}</div>
                <span className={`av ${riskClass(milestone.risk) === 'pos' ? 'acc' : ''}`}>
                  {milestone.patientCode.slice(-2)}
                </span>
                <div className="main">
                  <div className="t">{milestone.patientCode}</div>
                  <div className="s">{milestone.label}</div>
                </div>
                <span className={`pi-chip ${riskClass(milestone.risk)}`}>
                  <span className="d" />
                  {riskLabel(milestone.risk)}
                </span>
              </Link>
            ))}
          </div>

          <div className="pi-sec">
            Needs attention <Link className="more" to="/clinician/patients">All patients</Link>
          </div>
          <div className="pi-list">
            {(attention.length ? attention : view.patients).slice(0, 2).map((patient) => (
              <PatientRow key={patient.id} patient={patient} />
            ))}
          </div>

          <div className="pi-sec">Quick actions</div>
          <div className="pi-quick">
            <Link className="pi-qa" to="/clinician/log">
              <span className="ic acc">
                <PiIcon name="dose" />
              </span>
              <div className="tx">
                Log session<span>Trace dose and context</span>
              </div>
            </Link>
            <Link className="pi-qa" to="/clinician/patients">
              <span className="ic vio">
                <PiIcon name="intel" />
              </span>
              <div className="tx">
                Review signals<span>Human-confirmed only</span>
              </div>
            </Link>
          </div>
          <Disclaimer />
        </div>
      </>
    </PiScreen>
  );
}

export function ClinicianPatientsPage() {
  const view = useClinicianView();

  return (
    <PiScreen active="patients">
      <>
        <Head eyebrow={`${view.patients.length} active patients`} title="Patients" />
        <div className="pi-body">
          <div className="pi-input ph">
            <PiIcon name="search" />
            <span>Search by code or indication...</span>
          </div>
          <div className="pi-segctl clinician-seg-full">
            <button className="on" type="button">All</button>
            <button type="button">In follow-up</button>
            <button type="button">Overdue</button>
          </div>
          <div className="pi-list">
            {view.patients.map((patient) => (
              <PatientRow key={patient.id} patient={patient} />
            ))}
          </div>
        </div>
      </>
    </PiScreen>
  );
}

export function ClinicianPatientDetailPage() {
  const { patient } = useScopedPatient();

  if (!patient) {
    return <Navigate replace to="/clinician/patients" />;
  }

  const course = activeCourse(patient);
  const status = riskClass(patient.risk);

  return (
    <PiScreen
      active="patients"
      actionBar={
        <div className="pi-actionbar">
          <Link className="pi-btn pri" to={`/clinician/patients/${patient.id}/log-course`}>
            <PiIcon name="dose" />
            Log session
          </Link>
        </div>
      }
    >
      <>
        <PiNav
          backTo="/clinician/patients"
          right={
            <Link aria-label="Record outcome" className="navbtn" to={`/clinician/patients/${patient.id}/record-outcome`}>
              <PiIcon name="measure" />
            </Link>
          }
          title={patientDisplay(patient)}
        />
        <div className="pi-body clinician-actionbar-space">
          <div className="pi-card pad">
            <div className="clinician-patient-head">
              <span className="pi-avatar">{patient.initials}</span>
              <div>
                <div className="clinician-patient-title">{patientDisplay(patient)}</div>
                <div className="num clinician-patient-meta">
                  {patient.code} - {patient.age} - {patient.indication}
                </div>
              </div>
            </div>
            <dl className="pi-kv">
              <dt>Clinic</dt>
              <dd>{patient.clinicName}</dd>
              <dt>Current lot</dt>
              <dd className="mono">{courseLot(patient)}</dd>
              <dt>Stage</dt>
              <dd>Measure - active course</dd>
              <dt>Status</dt>
              <dd>{riskLabel(patient.risk)}</dd>
            </dl>
          </div>

          <div className="pi-sec">Loop progress</div>
          <LoopProgress current={patient.risk === 'stable' ? 5 : 4} />

          <div className="pi-sec">Response trajectory</div>
          <TrajectoryCard patient={patient} />

          <div className="pi-sec">Co-pilot read</div>
          <CopilotPreview patient={patient} />

          <div className="pi-sec">Progress photos</div>
          <PhotoPreview />

          <div className="pi-sec">Care episodes</div>
          <div className="pi-card pad">
            <div className="clinician-course-row">
              <span className="pi-cyc">C1</span>
              <strong>{course?.label ?? 'Course preview unavailable'}</strong>
              <span className={`pi-chip ${status}`}>
                <span className="d" />
                {course?.status ?? 'preview'}
              </span>
            </div>
            <p className="clinician-pi-note">{course?.productContext ?? 'Product context unavailable.'}</p>
            <p className="num clinician-pi-note">
              Next: {course?.nextMilestone ?? 'No milestone scheduled'}
              {course ? ` - ${safeDateLabel(course.nextMilestoneAt)}` : ''}
            </p>
          </div>

          <div className="pi-sec">Safety</div>
          <SafetyPreview patient={patient} />

          <div className="pi-sec">Discussion</div>
          <DiscussionPreview patient={patient} />

          <Disclaimer />
        </div>
      </>
    </PiScreen>
  );
}

export function ClinicianLogCoursePage() {
  const { patient } = useScopedPatient();

  if (!patient) {
    return <Navigate replace to="/clinician/patients" />;
  }

  const course = activeCourse(patient);

  return (
    <PiScreen
      active="log"
      actionBar={
        <div className="pi-actionbar">
          <Link className="pi-btn gho" to={`/clinician/patients/${patient.id}`}>
            Patient
          </Link>
          <Link className="pi-btn pri" to={`/clinician/patients/${patient.id}/record-outcome`}>
            Review outcome
          </Link>
        </div>
      }
    >
      <>
        <PiNav backTo={`/clinician/patients/${patient.id}`} title="Log session" />
        <div className="pi-body clinician-actionbar-space">
          <div className="pi-steps">
            <span className="sd done" />
            <span className="sd on" />
            <span className="sd" />
            <span className="sd" />
          </div>
          <div className="pi-sec">Scan</div>
          <div className="pi-scan">
            <div className="frame">
              <span className="tl" />
              <span className="tr" />
              <span className="bl" />
              <span className="br" />
            </div>
            <div className="laser" />
            <div className="hint">Point at the vial label or QR</div>
          </div>

          <div className="pi-sec">Context</div>
          <div className="pi-card pad clinician-log-context">
            <span className="av acc">
              <PiIcon name="dose" />
            </span>
            <div>
              <strong>{courseLot(patient)}</strong>
              <p className="num">Verified preview - no lot write performed</p>
            </div>
            <span className="pi-chip pos">
              <span className="d" />
              verified
            </span>
          </div>
          <div className="pi-pick on">
            <span className="rd" />
            <span className="av">{patient.initials}</span>
            <div>
              <strong>{patientDisplay(patient)}</strong>
              <p className="num clinician-pi-note">
                {patient.code} - {patient.indication} - {course?.label ?? 'Course preview'}
              </p>
            </div>
          </div>

          <div className="pi-sec">Form</div>
          <div className="pi-seg2">
            <div className="pi-opt">Left</div>
            <div className="pi-opt on">Right side</div>
          </div>
          <div className="pi-seg2 three">
            <div className="pi-opt on">1 of 1</div>
            <div className="pi-opt">Series</div>
            <div className="pi-opt">2 mL</div>
          </div>
          <div className="pi-field">
            <div className="lbl">Notes</div>
            <div className="pi-input area ph">Anatomic approach, guidance, tolerance...</div>
          </div>

          <div className="pi-sec">Review</div>
          <PreviewNote>
            This guided course log is a prototype capture surface. It does not persist course, lot,
            or photo data until the approved clinical write path is wired.
          </PreviewNote>
          <PhotoPreview compact />
        </div>
      </>
    </PiScreen>
  );
}

export function ClinicianOutcomePreviewPage() {
  const { patient } = useScopedPatient();
  const outcomesEnabled = isFeatureEnabled('liveOutcomes');
  const patientCode = patient?.code;
  const instrumentsState = useTypedOutcomeInstruments(outcomesEnabled);
  const measurementsState = useTypedOutcomeMeasurements({
    enabled: outcomesEnabled && !!patientCode,
    patientCode,
  });
  const instruments = instrumentsState.data ?? [];
  const defaultInstrument = instruments[0];
  const [selectedInstrumentId, setSelectedInstrumentId] = useState('');
  const [selectedTimepoint, setSelectedTimepoint] = useState(TIMEPOINTS[3] ?? 'Week 12');
  const [value, setValue] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const selectedInstrument =
    instruments.find((instrument) => instrument.id === selectedInstrumentId) ?? defaultInstrument;
  const endpointUnavailable = !!instrumentsState.error || !!measurementsState.error;
  const liveCapable = outcomesEnabled && !endpointUnavailable && instruments.length > 0;
  const canSubmit =
    liveCapable && !!patient && !!selectedInstrument && value.trim() !== '' && !submitting;

  if (!patient) {
    return <Navigate replace to="/clinician/patients" />;
  }

  async function handleSubmit() {
    if (!patient || !selectedInstrument || !canSubmit) return;
    setSubmitting(true);
    setSubmitMessage(null);
    setSubmitError(null);
    const trimmedValue = value.trim();
    const numericValue = Number(trimmedValue);
    const hasNumericValue = trimmedValue !== '' && Number.isFinite(numericValue);
    try {
      const response = await createOutcomeMeasurement({
        patient_id: patient.code,
        instrument_id: selectedInstrument.id,
        measured_at: new Date().toISOString(),
        timepoint_label: selectedTimepoint,
        value_numeric: hasNumericValue ? numericValue : null,
        value_text: hasNumericValue ? null : trimmedValue,
        notes: notes.trim() || null,
        source: 'manual',
      });
      measurementsState.appendMeasurement(response.data);
      setSubmitMessage(`Outcome measurement saved for ${patient.code}.`);
      setValue('');
      setNotes('');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unable to save outcome measurement');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PiScreen active="patients">
      <>
        <PiNav backTo={`/clinician/patients/${patient.id}`} title="Capture outcome" />
        <div className="pi-body">
          <div className="clinician-tier-row">
            <span className="pi-tier t2">
              <span className="dt" />
              Tier 2 - Preview
            </span>
            <span className="pi-state new">
              <span className="d" />
              {liveCapable ? 'Live-capable' : 'Preview'}
            </span>
          </div>
          <PreviewNote>
            Outcome capture uses the approved outcomes API when enabled. It stays in preview mode if
            the feature flag is off or the outcome endpoints are unavailable.
          </PreviewNote>

          <div className="pi-card pad clinician-log-context">
            <span className="pi-avatar">{patient.initials}</span>
            <div>
              <strong>{patientDisplay(patient)}</strong>
              <p className="num clinician-pi-note">
                {patient.code} - {patient.indication} - {courseLot(patient)}
              </p>
            </div>
          </div>

          <OutcomeTrajectorySummary
            enabled={outcomesEnabled}
            endpointUnavailable={endpointUnavailable}
            loading={instrumentsState.loading || measurementsState.loading}
            measurements={measurementsState.data ?? []}
            patient={patient}
          />

          <form aria-label="Outcome capture form" className={`pi-card ${liveCapable ? '' : 'pi-inert'}`}>
            {!liveCapable && (
              <div className="veil">
                <span className="tag">
                  <PiIcon name="clock" />
                  Preview until outcome wiring is approved
                </span>
              </div>
            )}
            <div className="clinician-outcome-form">
              {outcomesEnabled && instrumentsState.loading ? (
                <p className="clinician-pi-note">Loading approved outcome instruments...</p>
              ) : liveCapable ? (
                <>
                  <label className="pi-field">
                    <span className="lbl">Instrument</span>
                    <select
                      className="pi-input clinician-native-field"
                      id="clinician-outcome-instrument"
                      name="instrument_id"
                      value={selectedInstrument?.id ?? ''}
                      onChange={(event) => setSelectedInstrumentId(event.target.value)}
                    >
                      {instruments.map((instrument) => (
                        <option key={instrument.id} value={instrument.id}>
                          {instrument.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div>
                    <div className="lbl clinician-inline-label">Timepoint</div>
                    <div className="pi-states">
                      {TIMEPOINTS.map((timepoint) => (
                        <button
                          className={`o ${selectedTimepoint === timepoint ? 'sel' : ''}`}
                          key={timepoint}
                          type="button"
                          onClick={() => setSelectedTimepoint(timepoint)}
                        >
                          {timepoint}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="pi-field">
                    <span className="lbl">Value {instrumentRangeLabel(selectedInstrument)}</span>
                    <input
                      className="pi-input clinician-native-field"
                      id="clinician-outcome-value"
                      inputMode="decimal"
                      name="outcome_value"
                      placeholder="Enter value"
                      value={value}
                      onChange={(event) => setValue(event.target.value)}
                    />
                  </label>
                  <label className="pi-field">
                    <span className="lbl">Notes</span>
                    <textarea
                      className="pi-input area clinician-native-field"
                      id="clinician-outcome-notes"
                      name="notes"
                      rows={3}
                      value={notes}
                      onChange={(event) => setNotes(event.target.value)}
                      placeholder="Optional grounded observation; no direct identifiers."
                    />
                  </label>
                  <button className="pi-btn pri" disabled={!canSubmit} type="button" onClick={() => void handleSubmit()}>
                    {submitting ? 'Saving...' : 'Save outcome measurement'}
                  </button>
                </>
              ) : (
                <DisabledOutcomePreview />
              )}
            </div>
          </form>
          {submitMessage && (
            <p className="pi-disc" role="status">
              {submitMessage}
            </p>
          )}
          {submitError && (
            <p className="pi-disc" role="alert">
              Save failed: {submitError}
            </p>
          )}
          <Disclaimer />
        </div>
      </>
    </PiScreen>
  );
}

export function ClinicianLogPage() {
  const view = useClinicianView();
  const firstPatient = view.patients[0];

  if (firstPatient) {
    return <Navigate replace to={`/clinician/patients/${firstPatient.id}/log-course`} />;
  }

  return <Navigate replace to="/clinician" />;
}

export function ClinicianMePage() {
  const view = useClinicianView();
  const initials = view.physician.name
    .split(/[,\s]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('');

  return (
    <PiScreen active="profile">
      <>
        <Head eyebrow="Profile" title="Profile" />
        <div className="pi-body">
          <div className="pi-card pad clinician-profile-card">
            <span className="pi-avatar">{initials}</span>
            <div>
              <div className="clinician-patient-title">{view.physician.name}</div>
              <div className="num clinician-patient-meta">{view.physician.title}</div>
            </div>
          </div>

          <div className="pi-sec">Practice</div>
          <div className="pi-card pad">
            <dl className="pi-kv">
              <dt>Role</dt>
              <dd>Physician</dd>
              <dt>Clinics</dt>
              <dd>{view.physician.clinicIds.length}</dd>
              <dt>Active patients</dt>
              <dd>{view.patients.length}</dd>
              <dt>Courses</dt>
              <dd>{view.physician.administeredCourseIds.length}</dd>
            </dl>
          </div>

          <div className="pi-sec">Credentials</div>
          <div className="pi-list">
            {['Medical license', 'Product training', 'Procedure certification', 'Consent templates'].map((label) => (
              <div className="pi-row" key={label}>
                <span className="av acc">
                  <PiIcon name="check" />
                </span>
                <div className="main">
                  <div className="t">{label}</div>
                  <div className="s">Verified for demo scope</div>
                </div>
                <span className="pi-chip pos">
                  <span className="d" />
                  current
                </span>
              </div>
            ))}
          </div>

          <div className="pi-sec">Settings</div>
          <div className="pi-list">
            <div className="pi-row">
              <span className="av">
                <PiIcon name="clock" />
              </span>
              <div className="main">
                <div className="t">Activity log</div>
                <div className="s">Your audit trail</div>
              </div>
            </div>
            <div className="pi-row">
              <span className="av">
                <PiIcon name="alert" />
              </span>
              <div className="main">
                <div className="t">Adverse events</div>
                <div className="s">Report and review safety</div>
              </div>
            </div>
            <div className="pi-row">
              <span className="av">
                <PiIcon name="shield" />
              </span>
              <div className="main">
                <div className="t">Notifications</div>
                <div className="s">Follow-up and result alerts</div>
              </div>
            </div>
          </div>
        </div>
      </>
    </PiScreen>
  );
}

function LoopProgress({ current }: { current: number }) {
  return (
    <div className="pi-card pad">
      <div className="pi-loopstrip">
        {LOOP_STAGES.map((stage, index) => (
          <div className={`pi-loopnode ${index < current ? 'done' : ''} ${index === current ? 'cur' : ''}`} key={stage}>
            <span className="nd">
              <PiIcon name={index < 3 ? 'shield' : index === 3 ? 'dose' : index === 4 ? 'measure' : 'intel'} />
            </span>
            <span className="lb">{stage}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrajectoryCard({ patient }: { patient: ClinicianVisiblePatient }) {
  const status = riskClass(patient.risk);
  const values = patient.risk === 'urgent' ? [60, 57, 54, 52, 54] : patient.risk === 'watch' ? [58, 63, 67, 66, 68] : [52, 60, 70, 79, 84];
  const max = 100;
  const points = values
    .map((value, index) => {
      const x = 12 + (index / (values.length - 1)) * 250;
      const y = 124 - (value / max) * 100;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className="pi-card">
      <div className="pi-report">
        <div className="rhead">
          <span className={`big clinician-response-${status}`}>{responseIndex(patient)}</span>
          <span className="unit">response index</span>
          <span className={`pi-delta ${status}`}>{responseDelta(patient)} KOOS</span>
        </div>
        <div className="chart">
          <svg aria-label="Response trajectory" className="clinician-chart" viewBox="0 0 280 140">
            <path className="clinician-chart-grid" d="M12 24H268M12 74H268M12 124H268" />
            <polyline className={`clinician-chart-line ${status}`} points={points} />
            {values.map((value, index) => (
              <circle
                className={`clinician-chart-dot ${status}`}
                cx={12 + (index / (values.length - 1)) * 250}
                cy={124 - (value / max) * 100}
                key={`${value}-${index}`}
                r="4"
              />
            ))}
          </svg>
        </div>
        <div className="pi-provlegend">
          <span className="pi-prov measured">
            <span className="pd" />
            measured
          </span>
          <span className="pi-prov imported">
            <span className="pd" />
            imported
          </span>
          <span className="pi-prov reference">
            <span className="pd" />
            reference
          </span>
        </div>
      </div>
    </div>
  );
}

function CopilotPreview({ patient }: { patient: ClinicianVisiblePatient }) {
  const status = riskClass(patient.risk);
  const reviewText =
    patient.risk === 'stable'
      ? 'Trajectory is steady. No escalation surfaced by the preview read.'
      : patient.risk === 'watch'
        ? 'Response has flattened. Review the next outcome capture and current lot context.'
        : 'Recent symptom note needs same-day clinician review before any next step.';

  return (
    <div className="pi-copilot">
      <div className="pi-copilot-h">
        <span className="pi-copilot-mk">
          <PiIcon name="intel" />
        </span>
        <div>
          <div className="ti">Co-pilot read</div>
          <div className="su">second reviewer - {patientDisplay(patient)}</div>
        </div>
        <span className="right pi-livetag">
          <span className="dt" />
          Preview
        </span>
      </div>
      <div className="pi-copilot-sum">
        <div className="lead">
          <span className={`pi-riskdot ${status === 'pos' ? 'low' : 'med'}`}>
            {riskLabel(patient.risk)}
            <small>{status === 'pos' ? 'low' : 'med'}</small>
          </span>
          <div>{reviewText}</div>
        </div>
        <div className="pi-conf">
          <span className="cl">conf</span>
          <span className="cbar">
            <span style={{ width: patient.risk === 'stable' ? '82%' : '68%' }} />
          </span>
          <span className="cv">{patient.risk === 'stable' ? '0.82' : '0.68'}</span>
        </div>
      </div>
      <div className="pi-recs">
        <div className="pi-rec">
          <span className="rn">1</span>
          <div>Review patient-reported outcome history before changing plans.</div>
        </div>
        <div className="pi-rec">
          <span className="rn">2</span>
          <div>Associations shown here are signals to investigate, not efficacy claims.</div>
        </div>
      </div>
      <div className="pi-confirm">
        <div className="note">
          <b>You decide.</b> This preview never changes a patient status or plan.
        </div>
        <div className="acts">
          <button className="cbtn" type="button">Dismiss</button>
          <button className="cbtn pri" type="button">
            <PiIcon name="check" />
            File note
          </button>
        </div>
      </div>
    </div>
  );
}

function PhotoPreview({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`pi-card pad clinician-photo-strip ${compact ? 'compact' : ''}`}>
      <div className="pi-sitephoto">
        <PiIcon name="camera" />
      </div>
      <div>
        <strong>Photo documentation preview</strong>
        <p className="clinician-pi-note">
          Capture guidance only. No upload, diagnosis, or AI interpretation is connected in this
          slot.
        </p>
      </div>
    </div>
  );
}

function SafetyPreview({ patient }: { patient: ClinicianVisiblePatient }) {
  const urgent = patient.risk === 'urgent';

  return (
    <div className="pi-card pad clinician-log-context">
      <span className={`av ${urgent ? '' : 'acc'}`}>
        <PiIcon name={urgent ? 'alert' : 'check'} />
      </span>
      <div>
        <strong>{urgent ? 'Safety review needed' : 'No adverse events on record'}</strong>
        <p className="clinician-pi-note">
          {urgent
            ? patient.notes[0]?.body ?? 'Review before end of day.'
            : 'Clinician-visible notes do not report an active safety event.'}
        </p>
      </div>
    </div>
  );
}

function DiscussionPreview({ patient }: { patient: ClinicianVisiblePatient }) {
  return (
    <div className="pi-cm-card">
      <div className="pi-cm-row">
        <span className="av">
          <PiIcon name="comment" />
        </span>
        <div>
          <strong>Care-team discussion</strong>
          <p>{patient.notes[0]?.body ?? 'No clinician-visible notes yet.'}</p>
        </div>
      </div>
      <div className="pi-cm-compose">Reply is preview-only in this rebuild slot.</div>
    </div>
  );
}

function DisabledOutcomePreview() {
  return (
    <>
      <div className="clinician-inline-label">Instrument</div>
      <div className="pi-states">
        {FALLBACK_INSTRUMENTS.map((instrument, index) => (
          <span className={`o ${index === 0 ? 'sel' : ''}`} key={instrument}>
            {instrument}
          </span>
        ))}
      </div>
      <div className="clinician-inline-label">Timepoint</div>
      <div className="pi-states">
        {TIMEPOINTS.map((timepoint, index) => (
          <span className={`o ${index === 3 ? 'sel' : ''}`} key={timepoint}>
            {timepoint}
          </span>
        ))}
      </div>
      <div className="pi-seg2">
        <div className="pi-field">
          <div className="lbl">Value (0-100)</div>
          <div className="pi-input">
            <span className="num">72</span>
          </div>
        </div>
        <div className="pi-field">
          <div className="lbl">Provenance</div>
          <div className="pi-input">
            <span className="pi-prov measured">
              <span className="pd" />
              measured
            </span>
          </div>
        </div>
      </div>
      <button className="pi-btn pri" disabled type="button">
        Preview only
      </button>
    </>
  );
}

function OutcomeTrajectorySummary({
  enabled,
  endpointUnavailable,
  loading,
  measurements,
  patient,
}: {
  enabled: boolean;
  endpointUnavailable: boolean;
  loading: boolean;
  measurements: OutcomeMeasurementWithJoins[];
  patient: ClinicianVisiblePatient;
}) {
  const sortedMeasurements = useMemo(
    () =>
      [...measurements].sort(
        (a, b) => new Date(b.measured_at).getTime() - new Date(a.measured_at).getTime(),
      ),
    [measurements],
  );

  if (!enabled || endpointUnavailable || loading || sortedMeasurements.length === 0) {
    return (
      <div className="pi-card pi-preview">
        <span className="pi-preview-ribbon">PREVIEW</span>
        <TrajectoryCard patient={patient} />
      </div>
    );
  }

  return (
    <div className="pi-card pad">
      <div className="clinician-course-row">
        <strong>Recent measurements</strong>
        <span className="pi-chip pos">
          <span className="d" />
          live
        </span>
      </div>
      <ul className="clinician-measurement-list" aria-label="Live outcome measurements">
        {sortedMeasurements.slice(0, 4).map((measurement) => (
          <li key={measurement.id}>
            <strong>{measurement.instrument_name ?? measurement.instrument_code ?? 'Outcome'}</strong>
            <span>
              {formatMeasurementValue(measurement)} - {measurement.timepoint_label ?? 'timepoint'} -{' '}
              {safeDateLabel(measurement.measured_at)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function instrumentRangeLabel(instrument: OutcomeInstrument | undefined): string {
  if (!instrument) return '';
  const min = instrument.scale_min;
  const max = instrument.scale_max;
  if (typeof min === 'number' && typeof max === 'number') return `(${min}-${max})`;
  return instrument.unit ? `(${instrument.unit})` : '';
}

function formatMeasurementValue(measurement: OutcomeMeasurementWithJoins): string {
  if (measurement.value_numeric != null) return String(measurement.value_numeric);
  return measurement.value_text ?? 'Recorded';
}
